import type { ComponentType, CSSProperties } from 'react';
import { BadgeCheck, Check, FileText, MapPin, ShieldCheck, Sparkles, Wallet } from 'lucide-react';
import { Avatar, cn } from '@hrms/ui';
import type { IntroSlideId } from './introSlides.js';

/**
 * The picture in each intro slide's coloured hero: a few floating cards showing the product doing
 * the slide's claim, built from DOM rather than images so they stay sharp, follow the theme and
 * weigh nothing. Pure decoration — the slide's heading and points carry the meaning — so the
 * whole stage is aria-hidden and nothing in it is interactive.
 *
 * Each card is placed on a fixed 20x17rem stage (styles.css `.intro-stage`, scaled down on short
 * screens). `--i` orders the float-in when the slide arrives. No Tailwind translate utilities on a
 * card: the float-in animates the `translate` property and would undo them.
 */
export function IntroIllustration({ id }: { id: IntroSlideId }) {
  const Art = ART[id];
  return (
    <div className="intro-stage" aria-hidden="true" data-illustration={id}>
      <Art />
    </div>
  );
}

const ART: Record<IntroSlideId, ComponentType> = {
  team: TeamIllustration,
  payroll: PayrollIllustration,
  copilot: CopilotIllustration,
};

/** Arrival order for the float-in. */
const order = (i: number) => ({ '--i': i }) as CSSProperties;

/** A coloured initials circle — no stock photos in the repo, and the avatar palette is the product's. */
function Face({ name, size = 'md' }: { name: string; size?: 'sm' | 'md' }) {
  return <Avatar name={name} size={size} className="shrink-0" />;
}

function OnlineDot({ className }: { className?: string }) {
  return <span className={cn('block size-2 rounded-full bg-success', className)} />;
}

/** Slide 1 — the team's day, punched in from their phones. */
function TeamIllustration() {
  return (
    <>
      <div className="intro-float intro-card intro-tilt-left left-[3.25rem] top-[2.75rem] w-[13.5rem] p-3" style={order(1)}>
        <div className="flex items-center justify-between gap-2 rounded-xl bg-bg-subtle px-2.5 py-2">
          <span className="flex items-center gap-1.5 text-[0.8125rem] font-semibold">
            <OnlineDot />
            Engineering
          </span>
          <span className="intro-check rounded-full px-2 py-0.5 text-[0.6875rem] font-semibold">8 active</span>
        </div>
        {[
          ['Aarav M.', '09:02 AM'],
          ['Meera S.', '09:11 AM'],
        ].map(([name, time]) => (
          <div key={name} className="mt-2.5 flex items-center gap-2.5 px-1">
            <Face name={name!} size="sm" />
            <span className="min-w-0 flex-1 leading-tight">
              <span className="block text-[0.8125rem] font-semibold">{name}</span>
              <span className="block text-[0.75rem] text-fg-muted">{time}</span>
            </span>
            <OnlineDot />
          </div>
        ))}
        <div className="intro-check mt-3 rounded-xl px-2.5 py-2 text-[0.75rem] font-semibold">Team in sync</div>
      </div>

      <div className="intro-float intro-card -left-3 top-3 flex items-center gap-2.5 py-2 pl-2.5 pr-4" style={order(2)}>
        <span className="relative">
          <Face name="Ravi Kumar" />
          <OnlineDot className="absolute -bottom-0.5 -right-0.5 size-3 ring-2 ring-surface" />
        </span>
        <span className="leading-tight">
          <span className="block text-[0.8125rem] font-semibold">Ravi Kumar</span>
          <span className="block text-[0.6875rem] text-fg-muted">Lead Dev</span>
        </span>
      </div>

      <div className="intro-float intro-card -right-3 top-[10.5rem] flex items-center gap-2.5 py-2 pl-2.5 pr-4" style={order(3)}>
        <span className="relative">
          <Face name="Priya Sharma" />
          <span className="absolute -bottom-0.5 -right-0.5 block size-3 rounded-full bg-accent ring-2 ring-surface" />
        </span>
        <span className="leading-tight">
          <span className="block text-[0.8125rem] font-semibold">Priya Sharma</span>
          <span className="block text-[0.6875rem] text-accent">Product</span>
        </span>
      </div>

      <span className="intro-float intro-chip right-1 top-0" style={order(4)}>
        <span className="flex size-4 items-center justify-center rounded-full bg-success text-fg-on-accent">
          <Check className="size-3" strokeWidth={3} />
        </span>
        Punched in
      </span>

      <span className="intro-float intro-chip bottom-1 left-1" style={order(5)}>
        <MapPin className="size-3.5 text-accent" />
        Bengaluru HQ · 09:14 AM
      </span>
    </>
  );
}

/** Today's month for the payout card ("Nov 2026"): decoration, so the device's clock is fine. */
function payoutMonth(): string {
  return new Intl.DateTimeFormat('en-IN', { month: 'short', year: 'numeric' }).format(new Date());
}

/** Slide 2 — a salary paid, with the statutory pieces done around it. */
function PayrollIllustration() {
  return (
    <>
      <div className="intro-float intro-card left-[1.5rem] top-[2.5rem] w-[17rem] p-4" style={order(1)}>
        <div className="flex items-start gap-2.5">
          <span className="intro-check flex size-9 shrink-0 items-center justify-center rounded-full">
            <Wallet className="size-4" />
          </span>
          <span className="min-w-0 flex-1 leading-tight">
            <span className="block text-[0.6875rem] font-semibold tracking-wide text-fg-muted">SALARY PAYOUT</span>
            <span className="block text-[0.75rem] text-fg-subtle">{payoutMonth()}</span>
          </span>
          <span className="flex items-center gap-1 rounded-full bg-success-subtle px-2 py-0.5 text-[0.6875rem] font-semibold text-success">
            <span className="block size-1.5 rounded-full bg-success" />
            Disbursed
          </span>
        </div>
        <p className="mt-3 font-display text-[1.75rem] font-bold leading-none tracking-tight">₹1,42,850</p>
        <p className="mt-1.5 text-[0.75rem] text-fg-muted">Credited to bank •••• 4892</p>
        <div className="mt-3 flex h-2 overflow-hidden rounded-full">
          <span className="intro-bar-net block w-[62%]" />
          <span className="intro-bar-tax block w-[20%]" />
          <span className="intro-bar-retire block w-[18%]" />
        </div>
        <div className="mt-2 flex justify-between text-[0.6875rem] text-fg-muted">
          {[
            ['intro-bar-net', 'Net Pay'],
            ['intro-bar-tax', 'Taxes'],
            ['intro-bar-retire', 'Retirement'],
          ].map(([swatch, label]) => (
            <span key={label} className="flex items-center gap-1">
              <span className={cn('block size-2 rounded-full', swatch)} />
              {label}
            </span>
          ))}
        </div>
      </div>

      <span className="intro-float intro-chip intro-tilt-left left-0 top-0" style={order(2)}>
        PF <Check className="size-3.5 text-success" strokeWidth={3} />
      </span>
      <span className="intro-float intro-chip intro-tilt-right right-0 top-1" style={order(3)}>
        <FileText className="size-3.5 text-accent" />
        Form 16 ready
      </span>
      <span className="intro-float intro-chip bottom-2 left-1" style={order(4)}>
        ESI <Check className="size-3.5 text-success" strokeWidth={3} />
      </span>
      <span className="intro-float intro-chip intro-tilt-left bottom-0 right-2" style={order(5)}>
        <BadgeCheck className="size-3.5 text-success" />
        TDS <Check className="size-3.5 text-success" strokeWidth={3} />
      </span>
    </>
  );
}

/** Slide 3 — the assistant drafts, the person approves. */
function CopilotIllustration() {
  return (
    <>
      <div className="intro-float intro-card left-[1rem] top-[2.25rem] w-[18rem] p-3" style={order(1)}>
        <div className="flex items-center gap-2.5 rounded-xl bg-bg-subtle px-2.5 py-2">
          <span className="intro-approve flex size-7 items-center justify-center rounded-full">
            <Sparkles className="size-3.5" />
          </span>
          <span className="flex-1 text-[0.8125rem] font-semibold">PeopleOrbit AI</span>
          <span className="rounded-full bg-success-subtle px-2 py-0.5 text-[0.6875rem] font-semibold text-success">Live</span>
        </div>
        <div className="mt-2.5 flex justify-end">
          <span className="intro-user-bubble rounded-2xl rounded-br-md px-3 py-2 text-[0.8125rem]">
            Need Friday off for Diwali.
          </span>
        </div>
        <div className="mt-2 flex">
          <span className="max-w-[85%] rounded-2xl rounded-bl-md bg-bg-subtle px-3 py-2 text-[0.8125rem] leading-snug">
            Drafted Casual Leave for Friday. 3 days remaining.
          </span>
        </div>
        <div className="mt-2.5 flex gap-2">
          <span className="intro-approve flex flex-1 items-center justify-center gap-1 rounded-lg py-1.5 text-[0.8125rem] font-semibold">
            <Check className="size-3.5" strokeWidth={3} />
            Approve
          </span>
          <span className="flex items-center rounded-lg bg-bg-subtle px-3 py-1.5 text-[0.8125rem] font-medium text-fg-muted">
            Edit
          </span>
        </div>
      </div>

      <span className="intro-float intro-chip right-0 top-0" style={order(2)}>
        <ShieldCheck className="size-3.5 text-accent" />
        256-bit encrypted
      </span>
      <span className="intro-float intro-chip bottom-0 left-0" style={order(3)}>
        <span className="block size-2 rounded-full bg-success" />
        Human-in-the-loop <Check className="size-3.5 text-success" strokeWidth={3} />
      </span>
    </>
  );
}
